import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useParams } from "react-router";
import { useFormikContext } from "formik";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import snakeCase from "lodash/snakeCase";

import {
  formatAssetInput,
  prepareAssetInputWithCustomFields,
  buildAssetPatch,
  buildAssetBaseValues,
} from "../../../../utils/asset";
import useNotification from "../../../../hooks/notification";
import CaptureSkeleton from "../../../../components/CaptureSkeleton";
import CaptureActionsScrubber from "../../../../components/capture/CaptureActionsScrubber";
import TransferModal from "../../../../views/asset/TransferModal";
import { useAssetsCollection } from "../../../../contexts/assetsCollection";
import useProjectSpecificFields from "../../../../hooks/scrubber/useProjectSpecificFields";
import FormFields from "../../../../views/capture/FormFields";
import useTableSettings from "../../../../hooks/scrubber/useTableSettings";
import useTablePro from "../../../../components/TablePro/extensions/useTablePro";
import { usePermissions } from "../../../../contexts/permissions";
import { useCustomProjectFields } from "../../../../contexts/clients";
import { getFieldSettingValue } from "../../../../utils/fieldSettings";

const styles = {
  stickToBottom: {
    width: "100%",
    position: "sticky",
    bottom: "10px",
    left: 0,
    zIndex: 99,
    padding: "0",
    backgroundColor: "transparent",
  },
};

export default function FormPage({
  type,
  displayClient,
  displayProject,
  displayWarehouse,
  currentAsset,
  onCloseModal,
  openDuplicatedAsset,
  registerSaveHandler,
}) {
  const { can } = usePermissions();
  const { projectId: navProjectId } = useParams();
  const formProps = useFormikContext();
  const { notifyError, notifySuccess } = useNotification();
  const [openTransfer, setOpenTransfer] = useState(false);
  const {
    loading,
    loadingMutation,
    updateAsset,
    createAsset,
    createImage,
    removeAsset,
    handeRefetch,
    removeAssetsFromCollection,
  } = useAssetsCollection();
  const projectId = currentAsset?.project?.id;
  const settings = useTableSettings();
  const { fields: newFields } = useTablePro(settings);
  const defaultFields = useProjectSpecificFields(newFields);
  const projectFieldSettings = useCustomProjectFields({ projectId });
  const display = useMemo(() => {
    const fields = Object.fromEntries(
      defaultFields.map((field) => [snakeCase(field.id), field.visible])
    );
    fields.barcode_number = fields.barcode;
    fields.asset_label_pdf = getFieldSettingValue(projectFieldSettings, "asset_label_pdf");
    return fields;
  }, [defaultFields, projectFieldSettings]);

  const canWrite = can("assets.write");
  const canRemove = can("assets.remove");

  const handleSubmitAction = ({
    variables,
    setSubmitting,
    setStatus,
    setErrors,
    resetForm,
    values,
    submitAction,
    handleSubmit,
    rejectOnConflict = false,
  }) => {
    handleSubmit();
    setSubmitting(true);

    return submitAction({
      variables: {
        ...variables,
        input: formatAssetInput(variables.input),
        baseValues: variables.baseValues ? formatAssetInput(variables.baseValues) : undefined,
      },
      onSuccess: (response) => {
        setSubmitting(false);
        setStatus({ success: !response?.conflict });
        if (response?.conflict) {
          return;
        }
        resetForm({ values });
      },
      onFailure: (errors) => {
        setStatus({ success: false });
        setErrors(errors);
        notifyError(errors);
        setSubmitting(false);
      },
    }).then((response) => {
      const payload = response?.data ? Object.values(response.data)[0] : response;
      if (!response || payload?.errors?.length) {
        return Promise.reject(payload?.errors || false);
      }
      if (rejectOnConflict && payload?.conflict) {
        return Promise.reject(payload.conflict);
      }
      return response;
    });
  };

  const submitUpdateAsset =
    ({ values, ...props }, options = {}) =>
    () => {
      const patch = buildAssetPatch(values, formProps.initialValues);
      if (Object.keys(patch).length === 0) {
        notifySuccess("Asset was updated");
        return;
      }
      const formattedInput = prepareAssetInputWithCustomFields(patch, currentAsset);
      const formattedBaseValues = prepareAssetInputWithCustomFields(
        buildAssetBaseValues(patch, formProps.initialValues),
        currentAsset
      );
      const variables = {
        id: currentAsset?.id,
        input: formattedInput,
        baseValues: formattedBaseValues,
        expectedVersion: currentAsset?.lockVersion,
      };

      return handleSubmitAction({
        ...props,
        values,
        variables,
        submitAction: updateAsset,
        rejectOnConflict: options.rejectOnConflict,
      });
    };

  const submitCreateAsset =
    ({ values, ...props }) =>
    async () => {
      await submitUpdateAsset(formProps, { rejectOnConflict: true })({
        values,
        ...props,
      });
      const variables = { projectId, input: {} };
      return handleSubmitAction({ ...props, variables, submitAction: createAsset }).then(
        (response) => {
          const newAsset = response?.data?.createAsset?.asset;
          if (openDuplicatedAsset && newAsset) {
            openDuplicatedAsset(newAsset);
          }
          return response;
        }
      );
    };

  const submitDuplicateAsset =
    ({ values, ...props }) =>
    async () => {
      // Persist any in-progress edits to the original first. If this conflicts,
      // the rejection propagates and we never create a duplicate (no orphans).
      await submitUpdateAsset(formProps, { rejectOnConflict: true })({
        values,
        ...props,
      });
      // The server clones every copyable field off the persisted source asset
      // (barcode/photos excluded), so the client sends only the source id.
      const variables = {
        projectId: currentAsset.project.id,
        input: {},
        sourceAssetId: currentAsset.id,
      };
      return handleSubmitAction({ ...props, variables, submitAction: createAsset }).then(
        (response) => {
          const newAsset = response?.data?.createAsset?.asset;
          if (openDuplicatedAsset && newAsset) {
            openDuplicatedAsset(newAsset);
          }
          return response;
        }
      );
    };

  const submitRemoveAsset = () => {
    return removeAsset({
      variables: { id: currentAsset?.id },
      onSuccess: ({ asset }) => {
        notifySuccess("Asset was removed");
      },
    });
  };

  const toggleTransfer = useCallback(() => {
    setOpenTransfer((v) => !v);
  }, [setOpenTransfer]);

  const handleTransfer = ({ asset, projectId }) => {
    if (navProjectId && navProjectId !== projectId) {
      removeAssetsFromCollection(asset.id);
    }
  };

  useEffect(() => {
    if (typeof registerSaveHandler === "function") {
      registerSaveHandler(() => submitUpdateAsset(formProps)());
    }
  }, [registerSaveHandler, formProps.values]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form
      id="assetForm"
      className="d-flex flex-column flex-grow-1 justify-content-between justify-content-md-start"
      style={{ position: "relative" }}
    >
      {loading && (
        <Box py={1}>
          <LinearProgress className="mt-2" />
        </Box>
      )}
      {currentAsset?.id ? (
        <FormFields
          type={type}
          asset={currentAsset}
          loading={loadingMutation}
          displayClient={displayClient}
          displayProject={displayProject}
          displayWarehouse={displayWarehouse}
          updateAsset={updateAsset}
          createImage={createImage}
          setErrors={formProps.setErrors}
          errors={formProps.errors}
          overrideDisplayFields={display}
        />
      ) : (
        loading && <CaptureSkeleton />
      )}
      <Box mb={2} />
      <Box sx={styles.stickToBottom}>
        <CaptureActionsScrubber
          disabled={formProps.isSubmitting}
          onSave={submitUpdateAsset(formProps)}
          disableSave={!currentAsset?.id}
          onCreate={submitCreateAsset(formProps)}
          onDuplicate={submitDuplicateAsset(formProps)}
          projectId={projectId}
          onCloseModal={onCloseModal}
          onRemove={submitRemoveAsset}
          onTransfer={toggleTransfer}
          handeRefetch={handeRefetch}
          canWrite={canWrite}
          canRemove={canRemove}
        />
        {openTransfer && (
          <TransferModal
            asset={currentAsset}
            onTransfer={handleTransfer}
            onClose={toggleTransfer}
          />
        )}
      </Box>
    </form>
  );
}
